'use client';

// based on https://www.radix-ui.com/primitives/docs/components/toggle-group#api-reference

import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cx } from 'class-variance-authority';
import styles from './toggleGroup.module.scss';

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root>
>(({ className, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cx(styles.toggleGroup, className)}
    {...props}
  >
    {children}
  </ToggleGroupPrimitive.Root>
));

ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item>
>(({ children, ...props }, ref) => {
  return (
    <ToggleGroupPrimitive.Item className={styles.toggle} ref={ref} {...props}>
      {children}
    </ToggleGroupPrimitive.Item>
  );
});

ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };

type BaseToggleGroupOption = {
  value: string;
  children: React.ReactNode;
};

type BaseToggleGroupProps<T extends BaseToggleGroupOption> = {
  options: T[];
  canDeselect?: boolean;
} & React.ComponentProps<typeof ToggleGroup>;

export default function BaseToggleGroup<T>({
  onValueChange,
  canDeselect = true,
  ...props
}: BaseToggleGroupProps<T & BaseToggleGroupOption>) {
  return (
    <ToggleGroup
      onValueChange={(value: string & string[]) => {
        if (!value && !canDeselect) return;
        onValueChange?.(value);
      }}
      {...props}
    >
      {props.options.map((option) => (
        <ToggleGroupItem key={option.value} value={option.value}>
          {option.children}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
